import React, { useMemo, useState } from "react"
import classNames from "classnames"
import { AnimatePresence, motion } from "motion/react"
import Scroll from "@hg/scroll"

import { useTranslation } from "@/locales"
import { needFixWritingMode } from "@/utils/misc"
import BookContainer from "../../components/BookContainer"
import Exit from "../../components/Exit"
import Nav from "../../components/Nav"
import SubpageBg from "../../components/SubpageBg"
import TabButton from "../../components/TabButton"
import { ESubPage } from "../../context"
import { useTopicI18nStore } from "../../locales"

import styles from "./index.module.less"

const DANGER_LEVEL_MAP = {
    LOW: 0,
    MEDIUM: 1,
    HIGH: 2,
}

const PageEnemy: React.FC<{
    className?: string
    style?: React.CSSProperties
    onExit?: () => void
}> = ({ className, style, onExit }) => {
    const { enemyList } = useTopicI18nStore.getState().objects
    const enemyMap = useMemo(() => {
        return enemyList.reduce(
            (acc, item) => {
                acc[item.id] = item
                return acc
            },
            {} as Record<string, (typeof enemyList)[number]>
        )
    }, [enemyList])

    const [currentId, setCurrentId] = useState(enemyList[0].id)
    const { t } = useTranslation()
    const enemy = useMemo(() => enemyMap[currentId], [currentId, enemyMap])

    return (
        <motion.div
            className={classNames(
                styles.pageContainer,
                needFixWritingMode() && styles.safari,
                className
            )}
            style={style}
            initial={{
                opacity: 0,
                pointerEvents: "none",
            }}
            animate={{
                opacity: 1,
                pointerEvents: "auto",
            }}
            exit={{
                opacity: 0,
                pointerEvents: "none",
            }}
            transition={{
                duration: 0.5,
                ease: "easeInOut",
            }}
        >
            <SubpageBg />
            <BookContainer className={styles.bookContainer} type="enemy">
                <AnimatePresence mode="wait">
                    <motion.div
                        key={currentId}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{
                            duration: 0.5,
                            ease: "easeInOut",
                        }}
                    >
                        <motion.div
                            className={styles.image}
                            data-key={enemy.id}
                        ></motion.div>
                        <motion.div className={styles.title}>
                            <motion.div
                                className={styles.decoLight}
                            ></motion.div>

                            <motion.div
                                className={styles.decoCloud}
                            ></motion.div>
                            <motion.div
                                className={styles.decoText}
                            ></motion.div>
                            <motion.div className={styles.decoSub}></motion.div>
                            <motion.div className={styles.text}>
                                {enemy.name}
                            </motion.div>
                        </motion.div>
                        <motion.div className={styles.desc}>
                            <div
                                className={classNames(
                                    styles.text,
                                    enemy.desc.length > 120 ? styles.small : ""
                                )}
                            >
                                {enemy.desc}
                            </div>
                        </motion.div>
                        <motion.div className={styles.dangerLevel}>
                            <div className={styles.label}>
                                <div className={styles.cn}>
                                    {t("enemy.dangerLevel")}
                                </div>
                                <div className={styles.en}>
                                    {t("enemy.dangerLevel.pinyin")}
                                </div>
                            </div>
                            <div className={styles.icons}>
                                {new Array(3).fill(0).map((_, index) => (
                                    <div
                                        key={index}
                                        className={classNames(styles.icon, {
                                            [styles.active]:
                                                DANGER_LEVEL_MAP[
                                                    enemy.dangerLevel as keyof typeof DANGER_LEVEL_MAP
                                                ] >= index,
                                        })}
                                    ></div>
                                ))}
                            </div>
                            <div
                                className={classNames(
                                    styles.frame,
                                    enemy.dangerLevel.length >= 5 &&
                                        styles.small
                                )}
                            >
                                {enemy.dangerLevel}
                            </div>
                        </motion.div>
                    </motion.div>
                </AnimatePresence>
            </BookContainer>
            <Scroll
                className={styles.scrollContainer}
                direction="y"
                rotateDetection
            >
                <div className={styles.buttonList}>
                    {enemyList.map((item, index) => (
                        <TabButton
                            key={index}
                            label={item.name}
                            active={currentId === item.id}
                            onClick={() => setCurrentId(item.id)}
                        />
                    ))}
                </div>
            </Scroll>
            <Nav page={ESubPage.Enemy} />
            <Exit onClick={onExit} />
        </motion.div>
    )
}

export default PageEnemy
